// Configuración de las páginas legales públicas.
// El contenido real vive en docs/legal/<version>/<slug>.md (source of truth) y se
// sirve como estático (ver scripts/copy-legal.mjs):
//   /legal/<slug>            → versión VIGENTE
//   /legal/v3.0/<slug>       → histórico (§16: la anterior sigue accesible por URL)
//   /legal/plantillas/<name> → plantillas de aviso para el tenant (descarga)

export type LegalSlug =
  | 'aviso-de-privacidad'
  | 'aviso-simplificado'
  | 'terminos-y-condiciones'
  | 'tarifas';

export interface LegalDocMeta {
  slug: LegalSlug;
  title: string;
  navLabel: string;
}

export const LEGAL_DOCS: LegalDocMeta[] = [
  { slug: 'aviso-de-privacidad', title: 'Aviso de Privacidad', navLabel: 'Aviso de Privacidad' },
  { slug: 'aviso-simplificado', title: 'Aviso de Privacidad Simplificado', navLabel: 'Aviso Simplificado' },
  { slug: 'terminos-y-condiciones', title: 'Términos y Condiciones', navLabel: 'Términos y Condiciones' },
  // PR A (V204) — Tarifario (precios de lista IVA incluido, comisión 2%, folio excedente).
  // Forma parte de los TyC (§5.10, §6, §8); se publica sin gate de aceptación propio.
  { slug: 'tarifas', title: 'Tarifario', navLabel: 'Tarifario' },
];

export const LEGAL_BASE_PATH = '/legal';

// ---- Versionado (Legales v3.3) --------------------------------------------
/** Versión VIGENTE publicada en los slugs planos = carpeta más alta de docs/legal/.
 *  PR A (V204, relay §2.11a): sube a 3.3 (TyC ampliado §5–§8/§15 + Tarifario nuevo).
 *  En SINCRONÍA, el backend sube `SIGNUP_LEGAL_DOCS.terms`→`tyc-v3.3` y
 *  `CFDI_EMISOR_CONTRACT_DOC`→`tyc-3.3-s5` → la re-aceptación (V79) + el candado del
 *  emisor (V199) exigen la 3.3 YA. `LEGAL_EFFECTIVE_DATE` = 2026-11-07: la página
 *  muestra 3.3 como la versión a aceptar, con el banner "entra en vigor el …; hasta
 *  entonces rige la v3.2" + enlace a `/legal/v3.2`. La PROMOCIÓN a prod espera el correo
 *  de notificación a los tenants (acción de Arturo). */
export const CURRENT_LEGAL_VERSION = '3.3';
/** Versiones anteriores accesibles por URL (histórico, §16). Orden: más reciente primero. */
export const PRIOR_LEGAL_VERSIONS = ['3.2', '3.1', '3.0'] as const;
/** Fecha de entrada en vigor de la versión vigente (§16: 30 días naturales desde la
 *  notificación). Antes de esta fecha rige la versión anterior (v3.2). */
export const LEGAL_EFFECTIVE_DATE = '2026-11-07';
export const LEGAL_EFFECTIVE_DATE_LABEL = '7 de noviembre de 2026';

/** Relay Fidelidad v3 §2bis.6 — la versión que RIGE hasta la fecha de vigencia NO es la
 *  misma para todos los documentos: el TyC 3.3 sustituye al 3.2, pero el Aviso de FluidSol
 *  3.2 (publicado en la carpeta v3.3) sustituye al 3.1 (el paquete v3.2 no lo re-emitió;
 *  /legal/v3.2/aviso-* = texto 3.1). El banner remite a la anterior REAL de cada slug. */
export const PRIOR_VERSION_BY_SLUG: Partial<Record<LegalSlug, string>> = {
  'aviso-de-privacidad': '3.1',
  'aviso-simplificado': '3.1',
};
export const priorVersionFor = (slug: LegalSlug): string =>
  PRIOR_VERSION_BY_SLUG[slug] ?? PRIOR_LEGAL_VERSIONS[0];

/** '3.0' → 'v3.0' (nombre de carpeta / segmento de URL). */
export const versionFolder = (v: string) => `v${v}`;

export function legalPath(slug: LegalSlug): string {
  return `${LEGAL_BASE_PATH}/${slug}`;
}
/** URL de una versión específica: /legal/v3.0/<slug>. */
export function legalVersionPath(version: string, slug: LegalSlug): string {
  return `${LEGAL_BASE_PATH}/${versionFolder(version)}/${slug}`;
}

export function isLegalSlug(value: string): value is LegalSlug {
  return LEGAL_DOCS.some((d) => d.slug === value);
}
export function isPriorVersionFolder(value: string): string | null {
  const m = value.match(/^v(\d+\.\d+)$/);
  return m && (PRIOR_LEGAL_VERSIONS as readonly string[]).includes(m[1]) ? m[1] : null;
}
export function getLegalDoc(slug: string): LegalDocMeta | undefined {
  return LEGAL_DOCS.find((d) => d.slug === slug);
}

// ---- Plantillas de aviso de privacidad para el tenant ----------------------
export type TenantTemplateKey = 'cfdi' | 'fidelidad' | 'cfdi_lealtad';
export interface TenantTemplateMeta {
  key: TenantTemplateKey;
  file: string; // en /legal/plantillas/<file>.md
  title: string;
}
export const TENANT_TEMPLATES: Record<TenantTemplateKey, TenantTemplateMeta> = {
  cfdi: { key: 'cfdi', file: 'plantilla-tenant-cfdi', title: 'Plantilla de aviso de privacidad — CFDI' },
  fidelidad: { key: 'fidelidad', file: 'plantilla-tenant-fidelidad', title: 'Plantilla de aviso de privacidad — Fidelidad' },
  cfdi_lealtad: { key: 'cfdi_lealtad', file: 'plantilla-tenant-cfdi-lealtad', title: 'Plantilla de aviso de privacidad — CFDI y Lealtad' },
};
export function templatePath(file: string): string {
  return `${LEGAL_BASE_PATH}/plantillas/${file}.md`;
}
/** Selección de plantilla según capacidades activas del tenant. null = ninguna. */
export function pickTenantTemplate(hasCfdi: boolean, hasLoyalty: boolean): TenantTemplateMeta | null {
  if (hasCfdi && hasLoyalty) return TENANT_TEMPLATES.cfdi_lealtad;
  if (hasCfdi) return TENANT_TEMPLATES.cfdi;
  if (hasLoyalty) return TENANT_TEMPLATES.fidelidad;
  return null;
}

// ---- Parte D — plantillas v3.2 POR COMBINACIÓN de finalidades ---------------
// La abogada diseña una plantilla por combinación de funciones activas ("si activas
// otras funciones, usa la plantilla que las combine", TyC v3.2 §9.3). Las 7 se
// OFRECEN todas; el tenant elige la que combine SUS funciones. `covers` documenta
// qué finalidades cubre cada una (guía de las casillas — la atestación es del tenant,
// las casillas NUNCA se pre-marcan solas).
export interface TenantTemplateV32 {
  file: string;   // base name en /legal/plantillas/v<version>/<file>.{md,docx}
  /** Carpeta de la que se sirve: las 4 con fidelidad son v1.1 (docs/legal/v3.3/plantillas,
   *  relay Fidelidad v3 §2bis.7); las otras 3 no cambiaron (v3.2). */
  version: '3.3' | '3.2';
  title: string;  // nombre legible
  covers: { facturacion: boolean; reservasPagos: boolean; fidelidad: boolean };
}
export const TENANT_TEMPLATES_V32: TenantTemplateV32[] = [
  { file: 'plantilla_aviso_privacidad_tenants_venta_reserva', title: 'Reservas y pagos en línea', version: '3.2', covers: { facturacion: false, reservasPagos: true, fidelidad: false } },
  { file: 'plantilla_aviso_privacidad_tenants_cfdi', title: 'Facturación (CFDI)', version: '3.2', covers: { facturacion: true, reservasPagos: false, fidelidad: false } },
  { file: 'plantilla_aviso_privacidad_tenants_fidelidad', title: 'Fidelidad', version: '3.3', covers: { facturacion: false, reservasPagos: false, fidelidad: true } },
  { file: 'plantilla_aviso_privacidad_tenants_cfdi_venta_reserva', title: 'Facturación + reservas y pagos', version: '3.2', covers: { facturacion: true, reservasPagos: true, fidelidad: false } },
  { file: 'plantilla_aviso_privacidad_tenants_cfdi_fidelidad', title: 'Facturación + fidelidad', version: '3.3', covers: { facturacion: true, reservasPagos: false, fidelidad: true } },
  { file: 'plantilla_aviso_privacidad_tenants_fidelidad_venta_reserva', title: 'Fidelidad + reservas y pagos', version: '3.3', covers: { facturacion: false, reservasPagos: true, fidelidad: true } },
  { file: 'plantilla_aviso_privacidad_tenants_cfdi_fidelidad_venta_reserva', title: 'Facturación + fidelidad + reservas y pagos', version: '3.3', covers: { facturacion: true, reservasPagos: true, fidelidad: true } },
];
/** Ruta servida de una plantilla (md = fuente; docx = descarga para completar). La
 *  copia `scripts/copy-legal.mjs` (docs/legal/<vN>/plantillas → public/legal/plantillas/<vN>). */
export function templateV32Path(file: string, ext: 'md' | 'docx', version: '3.3' | '3.2' = '3.2'): string {
  return `${LEGAL_BASE_PATH}/plantillas/${versionFolder(version)}/${file}.${ext}`;
}
